class PredictiveSearch extends HTMLFormElement {
  /* Longest the drawer waits for the fallback suggested searches before painting without them. */
  static SUGGESTED_SEARCH_WAIT_MS = 600;

  /* Tagalys sections the drawer renders itself; any other section that comes back is
     treated as text suggestions. */
  static SUGGESTION_SECTIONS = ['products', 'collections', 'pages'];

  constructor() {
    super();
    this.cachedMap = new Map();
    this.focusElement = this.input;
    this.resetButton.addEventListener('click', this.clear.bind(this));
    this.input.addEventListener('input', FoxTheme.utils.debounce(this.onChange.bind(this), 300));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.input.addEventListener('keydown', this.onKeydown.bind(this));
    this.searchContent = this.querySelector('.search__content');
    this.statusElement = this.querySelector('[data-search-status]');
    this.searchRecommendationEmpty = this.dataset.searchRecommendationEmpty === 'true';
    this.activeOptionIndex = -1;

    const isTemplateSearch = this.closest('.template-search');
    if (isTemplateSearch) {
      document.addEventListener('click', this.handleClickOutside.bind(this));
    }

    /* Tagalys. Stays false when the SDK is disabled or has not loaded yet, in which
       case every path below falls back to native Shopify predictive search. */
    this.tagalysReady = false;
    this.connector = null;
    this.setupTagalys();
  }

  get input() {
    return this.querySelector('input[type="search"]');
  }
  get resetButton() {
    return this.querySelector('button[type="reset"]');
  }
  get resultsContainer() {
    return document.getElementById('PredictiveSearchResults-' + FoxTheme.utils.getSectionId(this));
  }

  /* ---------------------------------------------------------------- Tagalys */

  setupTagalys() {
    if (typeof window.onTagalysReady !== 'function') return;

    window.onTagalysReady((Tagalys) => {
      /* One connector per page, shared by every <form is="predictive-search"> (header
         drawer + the /search landing field). Responses are broadcast and only the form
         that issued the query paints them. */
      if (!window.tagalysSearchSuggestions) {
        /* As-you-type text suggestions (popular / attribute searches) live in
           account-specific sections whose ids come from Tagalys, set in theme settings.
           An id the account does not define comes back under `undefined_sections` and
           is harmless. */
        const textSections = {};
        this.textSuggestionSectionIds().forEach((id) => {
          textSections[id] = { count: 5 };
        });
        window.tagalysSearchSuggestions = Tagalys.APIConnector.SearchSuggestions.new({
          params: {
            sections: {
              products: { include: ['total_count', 'items'], count: parseInt(this.dataset.resultsLimit, 10) || 4 },
              /* Link sections configured on the Olive Grove account: `collections`
                 and `pages` return {title, link} items; `include` is only accepted on
                 `products`. Collections were added on the client's request; the
                 approved design (Figma 3822:48480 / 3822:48470) drew suggested
                 searches and pages only. */
              collections: { count: 5 },
              pages: { count: 5 },
              ...textSections,
            },
          },
          onSuccess: (response, helpers) => {
            document.dispatchEvent(new CustomEvent('tagalys:suggestions', { detail: { response, helpers } }));
          },
          onFailure: () => {
            document.dispatchEvent(new CustomEvent('tagalys:suggestions', { detail: { response: null } }));
          },
        });
      }
      this.connector = window.tagalysSearchSuggestions;
      this.tagalysReady = true;

      document.addEventListener('tagalys:suggestions', (event) => {
        if (window.tagalysActiveSuggestionForm !== this) return;
        this.renderTagalysSuggestions(event.detail.response);
      });
      this.addEventListener('submit', this.recordRecentSearch.bind(this));
      this.loadIdleSearches();
      this.loadIdleTrending(Tagalys);
    });
  }

  /* Idle state: trending products from a Tagalys smart widget (Theme settings > Tagalys
     search > Trending products smart widget ID). The merchant's picked products are
     already in the grid as the fallback; they are only replaced once the widget has
     returned products and its cards are built, so a slow or failed widget leaves the
     drawer exactly as it was. The widget is fetched once per page and shared by every
     predictive-search form. */
  loadIdleTrending(sdk) {
    const holder = this.querySelector('[data-tagalys-trending]');
    const grid = holder && holder.querySelector('[data-tagalys-trending-grid]');
    const widgetId = holder ? String(holder.dataset.tagalysTrendingId || '').trim() : '';
    const recommendations = sdk && sdk.APIConnector && sdk.APIConnector.Recommendations;
    if (!grid || !widgetId || !recommendations || typeof window.renderTagalysProductCards !== 'function') return;

    window.tagalysTrendingRequests = window.tagalysTrendingRequests || {};
    const count = parseInt(holder.dataset.count, 10) || 4;
    const key = `${widgetId}:${count}`;
    if (!window.tagalysTrendingRequests[key]) {
      window.tagalysTrendingRequests[key] = new Promise((resolve) => {
        try {
          recommendations.call({
            params: { recommendationId: widgetId, count },
            onSuccess: (response) => resolve((response && response.products) || []),
            onFailure: () => resolve([]),
          });
        } catch (error) {
          resolve([]);
        }
      });
    }

    window.tagalysTrendingRequests[key].then((products) => {
      if (!products.length) return;
      window
        .renderTagalysProductCards(products, {
          enableQuickAdd: false,
          showVendor: this.dataset.showVendor === 'true',
          imageSizes: '(max-width: 767px) 167px, 249px',
          sectionId: FoxTheme.utils.getSectionId(this),
        })
        .then((cards) => {
          if (!cards.length || !grid.isConnected) return;
          grid.replaceChildren(...cards);
        })
        .catch(() => {
          /* Picked products stay as the fallback. */
        });
    });
  }

  /* Idle state: Tagalys recent + popular searches. The merchant's Popular Searches /
     Popular Pages links stay in place unless Tagalys has entries to show. */
  loadIdleSearches() {
    const helpers = this.connector && this.connector.helpers;
    if (!helpers || typeof helpers.getRecentAndPopularSearches !== 'function') return;

    Promise.resolve(helpers.getRecentAndPopularSearches(5, 10))
      .then((data) => {
        const recent = (data && data.recentSearches) || [];
        const popular = (data && data.popularSearches) || [];
        const searchUrl = (window.tagalysConfig && window.tagalysConfig.searchUrl) || '/search';

        const linkList = (entries) =>
          entries
            .map((entry) => {
              const query = typeof entry === 'string' ? entry : entry.query;
              if (!query) return '';
              const href = `${searchUrl}?type=product&options%5Bprefix%5D=last&q=${encodeURIComponent(query)}`;
              return `<li><a class="search__link" href="${href}"><span class="search__link-label">${this.escape(query)}</span></a></li>`;
            })
            .join('');

        const popularGroup = this.querySelector('[data-tagalys-popular-group]');
        const popularList = this.querySelector('[data-tagalys-popular]');
        if (popular.length && popularList) {
          popularList.innerHTML = linkList(popular);
          if (popularGroup) popularGroup.hidden = false;
        }

        const recentGroup = this.querySelector('[data-tagalys-recent-group]');
        const recentList = this.querySelector('[data-tagalys-recent]');
        if (recent.length && recentGroup && recentList) {
          recentList.innerHTML = linkList(recent);
          recentGroup.hidden = false;
        }

        if ((popular.length && popularList) || (recent.length && recentList)) {
          this.searchContent?.classList.remove('hidden');
        }
      })
      .catch(() => {
        /* Idle links stay as the merchant configured them. */
      });
  }

  recordRecentSearch() {
    const term = this.getQuery();
    if (!term.length || !this.connector) return;
    try {
      const helpers = this.connector.helpers;
      helpers.recordRecentSearch(helpers.getEncodedQueryString({ query: term }));
    } catch (error) {
      /* Never block the submit on the recent-search write. */
    }
  }

  textSuggestionSectionIds() {
    const ids = (window.tagalysConfig && window.tagalysConfig.textSuggestionSections) || [];
    return ids.filter((id) => id && !PredictiveSearch.SUGGESTION_SECTIONS.includes(id));
  }

  /* Tagalys text suggestions: every returned section that is not products / collections
     / pages. Items carry `query` (search_queries) or `title` (links); a `link` or
     `queryString` keeps any filters the suggestion was built with. */
  tagalysTextSuggestions(sections) {
    const searchUrl = (window.tagalysConfig && window.tagalysConfig.searchUrl) || '/search';
    const entries = [];
    Object.keys(sections || {})
      .filter((key) => !PredictiveSearch.SUGGESTION_SECTIONS.includes(key))
      .forEach((key) => {
        ((sections[key] || {}).items || []).forEach((item) => {
          const text = item && (item.query || item.title);
          if (!text) return;
          let href = `${searchUrl}?q=${encodeURIComponent(text)}`;
          if (item.link) href = item.link;
          else if (item.queryString) href = `${searchUrl}?${item.queryString}`;
          entries.push({ text, href });
        });
      });
    return entries;
  }

  /* Fallback while the Tagalys account has no text-suggestion section: Shopify's own
     suggested searches, the list the native drawer showed in this slot. */
  fetchShopifySuggestedSearches(term) {
    this.suggestedSearchCache = this.suggestedSearchCache || new Map();
    if (this.suggestedSearchCache.has(term)) return this.suggestedSearchCache.get(term);

    const url = new URL(FoxTheme.routes.predictive_search_url + '.json', window.location.origin);
    url.searchParams.set('q', term);
    url.searchParams.set('resources[type]', 'query');
    url.searchParams.set('resources[limit]', 4);

    const request = fetch(url.toString())
      .then((response) => (response.ok ? response.json() : null))
      .then((data) => {
        const queries = (data && data.resources && data.resources.results && data.resources.results.queries) || [];
        return queries.filter((query) => query && query.text).map((query) => ({ text: query.text, href: query.url }));
      })
      .catch(() => []);
    this.suggestedSearchCache.set(term, request);
    return request;
  }

  suggestedSearchesMarkup(entries) {
    const strings = (window.tagalysCardStrings && window.tagalysCardStrings.search) || {};
    return (
      '<div class="search__terms predictive-search-result predictive-search-result--queries">' +
      `<h2 id="predictive-search-queries" class="search__col-title predictive-search-result__heading">${this.escape(strings.suggestedSearches || '')}</h2>` +
      '<ul id="predictive-search-results-queries-list" class="search__link-list" role="group" aria-labelledby="predictive-search-queries">' +
      entries
        .map(
          (entry, index) =>
            `<li id="predictive-search-option-query-${index + 1}" class="predictive-search-result__list-item" role="option" aria-selected="false">` +
            `<a href="${this.escape(entry.href)}" class="search__link" aria-label="${this.escape(entry.text)}"><span class="search__link-label">${this.escape(entry.text)}</span></a></li>`
        )
        .join('') +
      '</ul></div>'
    );
  }

  escape(value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  requestTagalys() {
    window.tagalysActiveSuggestionForm = this;
    this.setAttribute('loading', 'true');
    this.resetButton.classList.add('btn--loading');
    try {
      /* Started with the Tagalys query so the fallback suggested searches are ready when
         the response lands (cached per term). */
      this.fetchShopifySuggestedSearches(this.getQuery());
      this.connector.helpers.updateQuery(this.getQuery());
    } catch (error) {
      this.removeAttribute('loading');
      this.resetButton.classList.remove('btn--loading');
    }
  }

  /* Paints the Tagalys response with the same markup the native
     `predictive_search.performed` branch of predictive-search.liquid renders, so the
     drawer stylesheet and the combobox contract (role="option" items, non-tabbable
     links, aria-activedescendant, aria-live status) apply unchanged. */
  renderTagalysSuggestions(response) {
    const term = this.getQuery();
    /* A slow response must not resurrect results for a query since cleared, and a
       response for an earlier query must not overwrite a later one. */
    if (!term.length) return;
    if (response && response.query && String(response.query).trim() !== term) return;

    /* Suggested searches resolve before anything is painted, so the left column and the
       product cards arrive together. Tagalys text sections win; while the account has
       none, Shopify's suggested searches (requested alongside the Tagalys query) fill
       the slot, and a slow fallback never holds the drawer past
       PredictiveSearch.SUGGESTED_SEARCH_WAIT_MS. */
    const sections = (response && response.sections) || {};
    const hasContent = PredictiveSearch.SUGGESTION_SECTIONS.some(
      (key) => (((sections[key] || {}).items) || []).length > 0
    );
    const tagalysText = this.tagalysTextSuggestions(sections);
    this.tagalysRenderId = (this.tagalysRenderId || 0) + 1;
    const renderId = this.tagalysRenderId;

    let suggestedSearches;
    if (tagalysText.length || !hasContent) {
      suggestedSearches = Promise.resolve(tagalysText);
    } else {
      suggestedSearches = Promise.race([
        this.fetchShopifySuggestedSearches(term),
        new Promise((resolve) => setTimeout(() => resolve([]), PredictiveSearch.SUGGESTED_SEARCH_WAIT_MS)),
      ]);
    }

    suggestedSearches.then((textSuggestions) => {
      /* Superseded by a newer response, or the query moved on while waiting. */
      if (this.tagalysRenderId !== renderId || this.getQuery() !== term) return;
      this.paintTagalysSuggestions(response, term, textSuggestions);
    });
  }

  paintTagalysSuggestions(response, term, textSuggestions) {
    const target = this.resultsContainer;
    if (!target) return;

    const strings = (window.tagalysCardStrings && window.tagalysCardStrings.search) || {};
    const helpersJs = window.tagalysCardHelpers || {};
    const interpolate =
      helpersJs.interpolate ||
      ((tpl, values) =>
        Object.keys(values || {}).reduce((out, key) => out.split(`[${key}]`).join(values[key]), String(tpl || '')));
    const sections = (response && response.sections) || {};
    const products = (sections.products || {}).items || [];
    /* Link groups in drawer order, below the suggested searches. The list ids keep the
       native predictive-search markup's names so the drawer stylesheet and the combobox
       contract apply unchanged. */
    const linkGroups = [
      { key: 'collections', heading: strings.collections, list: 'collection', option: 'collection' },
      { key: 'pages', heading: strings.suggestedPages, list: 'article-page', option: 'page' },
    ]
      .map((group) => ({
        ...group,
        links: ((sections[group.key] || {}).items || []).filter((item) => item && item.title && item.link),
      }))
      .filter((group) => group.links.length);

    this.removeAttribute('loading');
    this.resetButton.classList.remove('btn--loading');
    this.searchContent?.classList.remove('hidden');
    this.setAttribute('results', 'true');

    const finish = () => {
      target.querySelectorAll('[role="option"] a, [role="option"] button').forEach((el) => {
        el.setAttribute('tabindex', '-1');
      });
      this.setActiveOption(-1);
      this.input.setAttribute('aria-expanded', 'true');
      this.announceResults(target);
    };

    if (!products.length && !linkGroups.length && !textSuggestions.length) {
      target.innerHTML =
        '<div class="search__no-results"><div class="search__no-results-text">' +
        `<h3 class="search__no-results-heading">${interpolate(strings.noResultsText, { terms: this.escape(term) })}</h3>` +
        `<p class="search__no-results-body m-0">${this.escape(strings.noResultsMessage)}</p>` +
        '</div></div>';
      finish();
      return;
    }

    const leftColumn = linkGroups.length || textSuggestions.length
      ? '<div class="search__drawer-col search__drawer-col--left">' +
        (textSuggestions.length ? this.suggestedSearchesMarkup(textSuggestions) : '') +
        linkGroups
          .map(
            (group) =>
              `<div class="search__terms predictive-search-result predictive-search-result--${group.key}">` +
              `<h2 id="predictive-search-${group.list}" class="search__col-title predictive-search-result__heading">${this.escape(group.heading || '')}</h2>` +
              `<ul id="predictive-search-results-${group.list}-list" class="search__link-list" role="group" aria-labelledby="predictive-search-${group.list}">` +
              group.links
                .map(
                  (item, index) =>
                    `<li id="predictive-search-option-${group.option}-${index + 1}" class="predictive-search-result__list-item" role="option" aria-selected="false">` +
                    `<a href="${this.escape(item.link)}" class="search__link"><span class="search__link-label">${this.escape(item.title)}</span></a></li>`
                )
                .join('') +
              '</ul></div>'
          )
          .join('') +
        '</div>'
      : '';

    const productsColumn = products.length
      ? '<div class="search__drawer-col search__drawer-col--right predictive-search-result predictive-search-result--products">' +
        '<div class="search__carousel-header">' +
        `<h2 id="predictive-search-products" class="search__col-title predictive-search-result__heading">${this.escape(strings.suggestedProducts)}</h2>` +
        `<button type="submit" class="search__results-all">${this.escape(strings.viewAll)}</button>` +
        '</div>' +
        '<div id="predictive-search-results-products-list" class="search__product-grid" role="group" aria-labelledby="predictive-search-products"></div>' +
        '</div>' +
        /* Mobile sticky footer CTA (.Drawer Footer 8375:103391), inside the swapped
           container so the count re-renders per query. */
        '<div class="search__drawer-footer">' +
        `<button type="submit" class="search__drawer-footer-cta">${interpolate(strings.viewAllCount, { count: products.length })}</button>` +
        '</div>'
      : '';

    target.innerHTML = '<div class="search__drawer-main">' + leftColumn + productsColumn + '</div>';

    const grid = target.querySelector('#predictive-search-results-products-list');
    if (!grid || typeof window.renderTagalysProductCards !== 'function') {
      finish();
      return;
    }

    window
      .renderTagalysProductCards(products, {
        enableQuickAdd: false,
        showVendor: this.dataset.showVendor === 'true',
        imageSizes: '(max-width: 767px) 167px, 249px',
        sectionId: FoxTheme.utils.getSectionId(this),
      })
      .then((cards) => {
        /* The query may have moved on while the cards were being built. */
        if (this.getQuery() !== term || !grid.isConnected) return;
        cards.forEach((card, index) => {
          const cell = document.createElement('div');
          cell.id = `predictive-search-option-product-${index + 1}`;
          cell.setAttribute('role', 'option');
          cell.setAttribute('aria-selected', 'false');
          cell.appendChild(card);
          grid.appendChild(cell);
        });
        finish();
      })
      .catch(finish);
  }

  /* ----------------------------------------------------------------- native */

  onFocus(event) {
    if (this.closest('.template-search')) {
      document.body.classList.add('predictive-search-open');
      if (this.getQuery().length === 0) {
        return;
      }
      if (this.tagalysReady) {
        this.requestTagalys();
        return;
      }
      const url = this.setupURL().toString();
      this.renderSection(url, event);
    }
  }
  getQuery() {
    return this.input.value.trim();
  }
  clear(event = null) {
    event && event.preventDefault();
    if (this.searchRecommendationEmpty && this.searchContent) {
      this.searchContent.classList.add('hidden');
    }
    this.input.value = '';
    this.input.focus();
    this.removeAttribute('results');
    this.removeAttribute('error');
    this.setActiveOption(-1);
    this.input.setAttribute('aria-expanded', 'false');
  }

  setupURL() {
    // Resolve against the current origin, not window.shopUrl: on `shopify theme
    // dev` (127.0.0.1:9292) shopUrl is the myshopify domain and the fetch dies
    // on CORS. Same-origin everywhere in production.
    const url = new URL(FoxTheme.routes.predictive_search_url, window.location.origin);
    return (
      url.searchParams.set('q', this.getQuery()),
      // Figma 3822:48480 / 3822:48470 draw exactly three result types - products,
      // query suggestions and pages. Collections and articles have no design peer
      // (scope brief section 12, delta J) and are not requested.
      url.searchParams.set('resources[type]', 'product,query,page'),
      url.searchParams.set('resources[limit]', this.dataset.resultsLimit || 4),
      url.searchParams.set('resources[limit_scope]', 'each'),
      // Predictive search omits SKU/barcode from its default field set, so a
      // typed SKU (e.g. TABMONTG240) found nothing here but matched on the full
      // /search page. Name every searchable field so SKU lookups work too.
      url.searchParams.set(
        'resources[options][fields]',
        'title,product_type,variants.title,vendor,variants.sku,variants.barcode,tag,author,body'
      ),
      url.searchParams.set('section_id', FoxTheme.utils.getSectionId(this)),
      url
    );
  }

  onChange() {
    if (this.getQuery().length === 0) {
      this.clear();
      return;
    }
    if (this.tagalysReady) {
      this.requestTagalys();
      return;
    }
    const url = this.setupURL().toString();
    this.renderSection(url);
  }

  renderSection(url) {
    this.cachedMap.has(url) ? this.renderSectionFromCache(url) : this.renderSectionFromFetch(url);
  }

  renderSectionFromCache(url) {
    const responseText = this.cachedMap.get(url);
    this.renderSearchResults(responseText), this.setAttribute('results', '');
  }

  renderSectionFromFetch(url) {
    // Abort any in-flight request so a slow "ch" response can never overwrite
    // a fast "chair" one (search-drawer brief section 6). Named fetchController:
    // ModalComponent already owns this.abortController for listener teardown.
    this.fetchController?.abort();
    this.fetchController = new AbortController();
    const { signal } = this.fetchController;

    // Indicate that loading has started
    this.setAttribute('loading', 'true');
    this.resetButton.classList.add('btn--loading');

    fetch(url, { signal })
      .then((response) => {
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        return response.text();
      })
      .then((responseText) => {
        // Process and cache the fetched data
        this.renderSearchResults(responseText);
        this.cachedMap.set(url, responseText);
        this.removeAttribute('error');
        // Only the success path flags results - the previous implementation set it
        // in finally, so the error state was also styled as having results.
        this.setAttribute('results', 'true');
      })
      .catch((error) => {
        // A deliberate abort (newer query or drawer close) is not an error state.
        if (error.name === 'AbortError') return;
        console.error('Error fetching data: ', error);
        this.setAttribute('error', 'Failed to load data');
      })
      .finally(() => {
        // Remove the loading attribute in both success and error cases
        this.removeAttribute('loading');
        this.resetButton.classList.remove('btn--loading');
      });
  }
  renderSearchResults(responseText) {
    const id = 'PredictiveSearchResults-' + FoxTheme.utils.getSectionId(this);
    const targetElement = document.getElementById(id);

    if (targetElement) {
      const parser = new DOMParser();
      const parsedDoc = parser.parseFromString(responseText, 'text/html');
      const contentElement = parsedDoc.getElementById(id);

      if (contentElement) {
        this.searchContent?.classList.remove('hidden');
        targetElement.innerHTML = contentElement.innerHTML;
        // Combobox pattern owns traversal (scope brief assertion 91): options and
        // their inner links/buttons are not tabbable - focus stays in the input and
        // aria-activedescendant reflects the active option.
        targetElement.querySelectorAll('[role="option"] a, [role="option"] button').forEach((el) => {
          el.setAttribute('tabindex', '-1');
        });
        this.setActiveOption(-1);
        this.input.setAttribute('aria-expanded', 'true');
        this.announceResults(targetElement);
      } else {
        console.error(`Element with id '${id}' not found in the parsed response.`);
      }
    } else {
      console.error(`Element with id '${id}' not found in the document.`);
    }
  }

  /* Announce the render outcome via the aria-live region OUTSIDE the swapped
     container (predictive-search.liquid [data-search-status]), so the announcement
     survives the innerHTML replacement. */
  announceResults(targetElement) {
    if (!this.statusElement) return;
    const strings = window.__predictiveSearchStrings || {};
    const count = targetElement.querySelectorAll('[role="option"]').length;
    let message;
    if (count > 0) {
      message = (strings.results || '[count] results for [terms]')
        .replace('[count]', count)
        .replace('[terms]', this.getQuery());
    } else {
      message = (strings.noResults || 'No results found for [terms]').replace('[terms]', this.getQuery());
    }
    this.statusElement.textContent = message;
  }

  /* Combobox keyboard traversal: linear across every [role="option"] in DOM order
     (queries -> pages -> products). Focus stays in the input; the active option is
     reflected via aria-activedescendant + aria-selected (scope brief assertions
     85-91). Options are not tabbable - the combobox pattern owns traversal. */
  get options() {
    const id = 'PredictiveSearchResults-' + FoxTheme.utils.getSectionId(this);
    const container = document.getElementById(id);
    return container ? Array.from(container.querySelectorAll('[role="option"]')) : [];
  }

  setActiveOption(index) {
    const options = this.options;
    options.forEach((option, i) => {
      option.setAttribute('aria-selected', i === index ? 'true' : 'false');
      option.classList.toggle('is-active', i === index);
    });
    this.activeOptionIndex = index;
    if (index >= 0 && options[index]) {
      this.input.setAttribute('aria-activedescendant', options[index].id);
      options[index].scrollIntoView({ block: 'nearest' });
    } else {
      this.input.removeAttribute('aria-activedescendant');
    }
  }

  onKeydown(event) {
    const options = this.options;
    if (options.length === 0) return;

    switch (event.key) {
      case 'ArrowDown': {
        event.preventDefault();
        const next = this.activeOptionIndex >= options.length - 1 ? 0 : this.activeOptionIndex + 1;
        this.setActiveOption(next);
        break;
      }
      case 'ArrowUp': {
        event.preventDefault();
        const prev = this.activeOptionIndex <= 0 ? options.length - 1 : this.activeOptionIndex - 1;
        this.setActiveOption(prev);
        break;
      }
      case 'Home':
        event.preventDefault();
        this.setActiveOption(0);
        break;
      case 'End':
        event.preventDefault();
        this.setActiveOption(options.length - 1);
        break;
      case 'Enter': {
        if (this.activeOptionIndex >= 0 && options[this.activeOptionIndex]) {
          const link = options[this.activeOptionIndex].querySelector('a[href]');
          if (link) {
            event.preventDefault();
            window.location.assign(link.href);
          }
        }
        // No active option: fall through to the native form submit.
        break;
      }
      case 'Escape':
        // Shell behaviour (search-drawer close) - never swallowed here.
        break;
    }
  }

  handleClickOutside(event) {
    if (!this.contains(event.target)) {
      setTimeout(() => {
        document.body.classList.remove('predictive-search-open');
      });
    }
  }
}
customElements.define('predictive-search', PredictiveSearch, { extends: 'form' });

class SearchDrawer extends DrawerComponent {
  constructor() {
    super();
    // QA P1 fix: FoxTheme.a11y.trapFocus derives first/last from ALL
    // focusables, including hidden ones (e.g. the mobile footer CTA at
    // desktop), so its wrap never fires from the last VISIBLE element and
    // focus escapes the drawer. Intercept Tab here (capture runs before the
    // vendor's document-level handler) and wrap across visible focusables.
    this.addEventListener('keydown', (event) => {
      if (event.code?.toUpperCase() !== 'TAB' || !this.open) return;
      const visible = FoxTheme.a11y.getFocusableElements(this).filter(
        (el) => el.offsetParent !== null || el.getClientRects().length > 0
      );
      if (visible.length === 0) return;
      const first = visible[0];
      const last = visible[visible.length - 1];
      if (!event.shiftKey && event.target === last) {
        event.preventDefault();
        event.stopPropagation();
        first.focus();
      } else if (event.shiftKey && (event.target === first || event.target === this)) {
        event.preventDefault();
        event.stopPropagation();
        last.focus();
      }
    });
  }
  get requiresBodyAppended() {
    return false;
  }
  hide() {
    if (this.open) {
      // Abort any in-flight predictive fetch so a resolving response cannot
      // repaint the results while (or after) the drawer closes. On the Tagalys
      // path there is no fetchController; its stale-response guard in
      // renderTagalysSuggestions covers the same case.
      this.querySelector('form[is="predictive-search"]')?.fetchController?.abort();
    }
    return super.hide();
  }
  get input() {
    return this.querySelector('input[type="search"]');
  }
  get focusElement() {
    return this.querySelector('input[type="search"]');
  }
}
customElements.define('search-drawer', SearchDrawer);
